import { buffer, coordAll, feature, multiPolygon, simplify, truncate, union } from '@turf/turf'
import { isEqual } from 'es-toolkit'
import type { Feature, Geometry, MultiPolygon, Polygon, Position } from 'geojson'

// The LTG API rejects report geometries with more vertices than this
export const MAX_VERTEX_COUNT = 500
export const DEFAULT_BUFFER_STEPS = 8

// The result may cover at most this multiple of the radius that was asked for
export const MAX_RADIUS_MULTIPLE = 1.25

// The longest a degree of latitude gets; a degree of longitude is shorter still, so this bounds
// how far simplification can have moved a vertex
const MAX_KM_PER_DEGREE = 111.7

// Within this multiple of the radius asked for, a narrower plan is not worth looking for
const CLOSE_ENOUGH_RADIUS_MULTIPLE = 1.01

const TOLERANCE_LADDER_RUNGS = 11
const BISECTION_ROUNDS = 7

export type BufferReportGeometryResult =
	| { success: true; geometry: MultiPolygon }
	| { success: false; reason: 'geometry-too-complex' | 'geometry-empty' }

// What the caller asked for, threaded through the search
type Search = {
	geometry: Geometry | undefined
	pointBuffersAt: ((steps: number) => MultiPolygon | undefined) | undefined
	radiusKm: number
	maxVertexCount: number
	bufferSteps: number
}

// A way of bringing the buffer inside the vertex budget: how far to simplify the asset, and how many
// segments to spend on each quarter circle
type Plan = { toleranceDegrees: number; steps: number }

// A buffer that fits the budget, and the radius its plan ended up covering
type Fit = { geometry: MultiPolygon; widenedRadiusKm: number }

/**
 * Buffers the geometry by `radiusKm`, staying within `maxVertexCount`. The result always covers the
 * whole radius and never covers more than `MAX_RADIUS_MULTIPLE` of it; a geometry that will not fit
 * inside that is reported as too complex.
 */
export const bufferWithinVertexLimit = (
	geometry: Geometry,
	radiusKm: number,
	opts: { maxVertexCount: number; bufferSteps: number },
): BufferReportGeometryResult => {
	if (!Number.isFinite(radiusKm) || radiusKm < 0) return { success: false, reason: 'geometry-empty' }
	// Without a radius around it the asset is the whole report, so it has to be a shape with area
	if (radiusKm === 0 && !coversArea(geometry)) return { success: false, reason: 'geometry-empty' }

	const { points, simplifiable } = splitParts(geometry)
	const search: Search = {
		geometry: simplifiable,
		pointBuffersAt: pointBuffersAt(points, radiusKm),
		radiusKm,
		...opts,
	}
	// Points stay at the radius asked for, so that their buffers are reused. When that leaves too little of the budget
	// for the rest, the points are buffered along with everything else, as wide as the plan makes it.
	const narrowest =
		narrowestFittingBuffer(search) ??
		(points && simplifiable ? narrowestFittingBuffer({ ...search, geometry: geometry, pointBuffersAt: undefined }) : undefined)
	if (!narrowest) return { success: false, reason: 'geometry-too-complex' }

	return { success: true, geometry: truncate(narrowest, { precision: radiusKm < 2 ? 5 : 4 }) }
}

/**
 * An asset usually buffers to more vertices than the budget allows, and there are two ways to give
 * some back: simplify the asset, or draw the round parts of the buffer more coarsely. Both lose
 * coverage, and both are paid for by widening the radius. They trade against each other, so the two
 * are searched together, and narrowest means the winner reaches least far past the radius asked for.
 */
const narrowestFittingBuffer = (search: Search) => {
	const widestAllowedKm = search.radiusKm * MAX_RADIUS_MULTIPLE
	const closeEnoughKm = search.radiusKm * CLOSE_ENOUGH_RADIUS_MULTIPLE
	let narrowest: Fit | undefined

	for (let steps = search.bufferSteps; steps >= 1; steps--) {
		// Coarsening reaches this far before any simplification is paid for on top, so once it is out of
		// range on its own, every coarser buffer below it is too
		const radiusWithoutSimplifying = search.radiusKm * circumscribeFactor(steps)
		if (radiusWithoutSimplifying > widestAllowedKm) break
		if (narrowest && radiusWithoutSimplifying >= narrowest.widenedRadiusKm) break

		const fit = narrowestFitAtStepCount(search, steps)
		if (fit && (!narrowest || fit.widenedRadiusKm < narrowest.widenedRadiusKm)) narrowest = fit

		// Near enough to the radius asked for that a coarser buffer is not worth looking at
		if (narrowest && narrowest.widenedRadiusKm <= closeEnoughKm) break
	}

	return narrowest?.geometry
}

// The least simplification that fits at this step count: a doubling ladder to bracket it, then
// bisection so the padding is no larger than it has to be
const narrowestFitAtStepCount = (search: Search, steps: number) => {
	const untouched = tryPlan(search, { toleranceDegrees: 0, steps })
	if (untouched) return untouched

	const bracket = search.geometry && bracketFittingTolerance(search.geometry, search, steps)
	return bracket && narrowTolerance(search, steps, bracket)
}

// Doubles the tolerance until the buffer fits, leaving a bracket the answer sits inside
const bracketFittingTolerance = (simplifiable: Geometry, search: Search, steps: number) => {
	const ceiling = toleranceCeiling(search.radiusKm, steps)
	if (ceiling <= 0) return undefined

	let tooLittle = 0
	let pointCount = coordAll(simplifiable).length
	for (let tolerance = ceiling / 2 ** TOLERANCE_LADDER_RUNGS; tolerance <= ceiling; tolerance *= 2) {
		const simplifiedPointCount = coordAll(simplifiedBy(simplifiable, tolerance)).length
		// A tolerance that drops no vertices cannot help, and paying for it would only widen the result
		if (simplifiedPointCount === pointCount) continue
		pointCount = simplifiedPointCount

		const fit = tryPlan(search, { toleranceDegrees: tolerance, steps })
		if (fit) return { fit, tooLittle, enough: tolerance }
		tooLittle = tolerance
	}

	return undefined
}

// Halves the bracket back down, because the ladder doubles and so can overshoot what was needed
const narrowTolerance = (search: Search, steps: number, bracket: { fit: Fit; tooLittle: number; enough: number }) => {
	let { fit, tooLittle, enough } = bracket

	for (let round = 0; round < BISECTION_ROUNDS; round++) {
		const middle = (tooLittle + enough) / 2
		const narrowed = tryPlan(search, { toleranceDegrees: middle, steps })
		if (narrowed) {
			enough = middle
			fit = narrowed
		} else {
			tooLittle = middle
		}
	}

	return fit
}

// Buffers under the plan, keeping the result only if it lands inside the vertex budget
const tryPlan = (search: Search, plan: Plan): Fit | undefined => {
	const simplified = search.geometry && simplifiedBy(search.geometry, plan.toleranceDegrees)

	const widenedRadiusKm = widenedRadiusFor(plan, search.radiusKm)
	const areaBuffer = simplified && bufferToMultiPolygon(simplified, widenedRadiusKm, plan.steps)
	const pointBuffer = search.pointBuffersAt?.(plan.steps)
	if ((simplified && !areaBuffer) || (search.pointBuffersAt && !pointBuffer)) return undefined

	const buffered = mergeBuffers(areaBuffer, pointBuffer)
	if (!buffered || buffered.coordinates.flat(2).length > search.maxVertexCount) return undefined
	return { geometry: buffered, widenedRadiusKm }
}

/**
 * The radius a plan actually has to cover. Simplifying moves a vertex by at most the tolerance, and
 * turf inscribes the buffer in the circle rather than around it, so the radius absorbs both and the
 * result still covers everything that was selected.
 */
const widenedRadiusFor = (plan: Plan, radiusKm: number) =>
	(radiusKm + plan.toleranceDegrees * MAX_KM_PER_DEGREE) * circumscribeFactor(plan.steps)

// The coarsest simplification a step count can still afford before the result reaches too far
const toleranceCeiling = (radiusKm: number, steps: number) =>
	((MAX_RADIUS_MULTIPLE / circumscribeFactor(steps) - 1) * radiusKm) / MAX_KM_PER_DEGREE

export const coversArea = (geometry: Geometry): boolean =>
	geometry.type === 'GeometryCollection'
		? geometry.geometries.every(coversArea)
		: geometry.type === 'Polygon' || geometry.type === 'MultiPolygon'

const simplifiedBy = (geometry: Geometry, tolerance: number) =>
	tolerance > 0 ? simplify(feature(geometry), { tolerance }).geometry : geometry

export const combineGeometries = (geometries: Geometry[]): Geometry | undefined =>
	geometries.length > 1 ? { type: 'GeometryCollection', geometries } : geometries[0]

// How far past the radius the buffer's vertices have to sit for its edges to reach the radius
const circumscribeFactor = (steps: number) => 1 / Math.cos(Math.PI / (4 * steps))

const isPoint = (geometry: Geometry) => geometry.type === 'Point' || geometry.type === 'MultiPoint'

// Points cannot be simplified, so they are kept apart from the lines and areas
const splitParts = (geometry: Geometry): { points?: Geometry; simplifiable?: Geometry } => {
	if (geometry.type !== 'GeometryCollection') return isPoint(geometry) ? { points: geometry } : { simplifiable: geometry }
	return {
		points: combineGeometries(geometry.geometries.filter(isPoint)),
		simplifiable: combineGeometries(geometry.geometries.filter((member) => !isPoint(member))),
	}
}

const groupByType = (geometry: Geometry): Geometry[] => {
	if (geometry.type !== 'GeometryCollection') return [geometry]

	const points: Position[] = []
	const lines: Position[][] = []
	const polygons: Position[][][] = []
	const collect = (geometry: Geometry) => {
		switch (geometry.type) {
			case 'Point':
				points.push(geometry.coordinates)
				break
			case 'MultiPoint':
				points.push(...geometry.coordinates)
				break
			case 'LineString':
				lines.push(geometry.coordinates)
				break
			case 'MultiLineString':
				lines.push(...geometry.coordinates)
				break
			case 'Polygon':
				polygons.push(geometry.coordinates)
				break
			case 'MultiPolygon':
				polygons.push(...geometry.coordinates)
				break
			case 'GeometryCollection':
				geometry.geometries.forEach(collect)
				break
		}
	}
	collect(geometry)

	return [
		...(points.length > 0 ? [{ type: 'MultiPoint' as const, coordinates: points }] : []),
		...(lines.length > 0 ? [{ type: 'MultiLineString' as const, coordinates: lines }] : []),
		...(polygons.length > 0 ? [{ type: 'MultiPolygon' as const, coordinates: polygons }] : []),
	]
}

const bufferOne = (geometry: Geometry, radiusKm: number, steps: number) =>
	buffer({ type: 'FeatureCollection', features: [feature(geometry)] }, radiusKm, { units: 'kilometers', steps })?.features[0]

const asMultiPolygon = (geometry: Polygon | MultiPolygon) =>
	geometry.type === 'Polygon' ? multiPolygon([geometry.coordinates]).geometry : geometry

const unionAsMultiPolygon = (features: Feature<Polygon | MultiPolygon>[]): MultiPolygon | undefined => {
	const merged = features.length > 1 ? union({ type: 'FeatureCollection', features })?.geometry : features[0]?.geometry
	return merged && asMultiPolygon(merged)
}

const bufferToMultiPolygon = (geometry: Geometry, radiusKm: number, steps: number): MultiPolygon | undefined =>
	unionAsMultiPolygon(
		groupByType(geometry).flatMap((group) => bufferOne(group, radiusKm, steps) ?? []) as Feature<Polygon | MultiPolygon>[],
	)

const mergeBuffers = (a: MultiPolygon | undefined, b: MultiPolygon | undefined): MultiPolygon | undefined =>
	a && b ? unionAsMultiPolygon([feature(a), feature(b)]) : (a ?? b)

type RememberedPoints = { points: Geometry; radiusKm: number; bySteps: Map<number, MultiPolygon | undefined> }
let rememberedPoints: RememberedPoints | undefined

// Buffers the points once for each step count, and remembers the last points it was asked about, so that
// adding a line to a selection does not buffer them again.
export const pointBuffersAt = (
	points: Geometry | undefined,
	radiusKm: number,
): ((steps: number) => MultiPolygon | undefined) | undefined => {
	if (!points) return undefined
	if (!rememberedPoints || rememberedPoints.radiusKm !== radiusKm || !isEqual(rememberedPoints.points, points)) {
		rememberedPoints = { points, radiusKm, bySteps: new Map() }
	}

	const { bySteps } = rememberedPoints
	return (steps) => {
		if (!bySteps.has(steps)) bySteps.set(steps, bufferToMultiPolygon(points, radiusKm * circumscribeFactor(steps), steps))
		return bySteps.get(steps)
	}
}
